import { logger } from '@forest/logger';
import { DEFAULT_COOKIE_CONSENT, useCookieConsent } from '@ophelos/shared/hooks/use-cookie-consent';
import { CookieConsentBanner } from '@ophelos/ui/components/cookie-consent-banner';
import { COOKIE_CONSENT_COPY } from '@ophelos/ui/components/legal/cookie-consent-copy';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { getCoreApiBaseUrl } from '@/lib/core-api-url';
import { getBoundCell, subscribeBoundCell } from '@/lib/global/cell-store';
import { isGlobalSurface } from '@/lib/surface-mode';

// Hub has no i18n; its UI is English.
const COPY = COOKIE_CONSENT_COPY.en;

interface CookieConsentContextValue {
  /** Reopen the banner in manage mode (persistent "Cookie settings" entry). */
  openSettings: () => void;
  /** Whether the fixed bottom banner is currently showing (so pages can reserve space for it). */
  bannerOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  // The staff's WorkOS org resolves to the operator server-side, so no build-time
  // operator env is needed. A pre-login decision persists locally and its audit
  // receipt is sent by the hook as soon as the org id becomes available after
  // sign-in — the choice is never left unlogged.
  const { organizationId } = useAuth();
  const boundCell = useSyncExternalStore(subscribeBoundCell, getBoundCell, getBoundCell);

  const consent = useCookieConsent({
    config: DEFAULT_COOKIE_CONSENT,
    sourceApp: 'hub',
    organizationId: organizationId ?? undefined,
    coreApiBaseUrl: isGlobalSurface() ? (boundCell?.origins.core_api ?? '') : getCoreApiBaseUrl(),
  });

  const categories = useMemo(
    () => consent.categories.map((c) => ({ ...c, description: COPY.categories.find((x) => x.key === c.key)?.body })),
    [consent.categories]
  );

  // Reflect the analytics grant into Datadog RUM live (grant starts collection,
  // revoke stops it). Gated on validity, so an expired or superseded-policy grant
  // stops tracking here even before the visitor re-consents.
  useEffect(() => {
    logger.setTrackingConsent(consent.analyticsGranted);
  }, [consent.analyticsGranted]);

  const value = useMemo<CookieConsentContextValue>(
    () => ({ openSettings: consent.reopen, bannerOpen: consent.open }),
    [consent.reopen, consent.open]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieConsentBanner
        open={consent.open}
        mode={consent.mode}
        categories={categories}
        // Fall back to the in-app policy page when no operator policy URL is configured.
        policyUrl={consent.policyUrl ?? '/cookie-policy'}
        intro={COPY.intro}
        ui={COPY.ui}
        declineNote={COPY.declineNote}
        initialConsent={consent.consent}
        onAcceptAll={consent.acceptAll}
        onRejectAll={consent.rejectAll}
        onSave={consent.save}
        onOpenChange={consent.onOpenChange}
      />
    </CookieConsentContext.Provider>
  );
}

/** Access the persistent "Cookie settings" entry point. Returns null outside the provider. */
export function useCookieConsentSettings(): CookieConsentContextValue | null {
  return useContext(CookieConsentContext);
}
